import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarDay } from "react-icons/fa";
import DashboardLayout from "../layouts/DashboardLayout";
import getApiUrl from "../utils/api";
import "./Summarize.css";

const toLocalDateKey = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const filterChecklistForToday = (items, todayKey) => {
  if (!Array.isArray(items)) return [];
  return items.filter((item) => {
    if (!item) return false;
    const occurrenceKey = toLocalDateKey(item.occurrenceDate);
    if (!occurrenceKey) {
      return !item.isRecurring;
    }
    return occurrenceKey === todayKey;
  });
};

const getTaskStatusKey = (status) =>
  (status || "unknown").toLowerCase().replace(/\s+/g, "-");

const isHighPriorityTask = (task) => {
  const priority = String(task?.priority || "").trim().toLowerCase();
  return priority === "high" || priority === "critical" || priority === "urgent";
};

const isDueToday = (task, todayKey) => {
  const dueKey =
    toLocalDateKey(task?.due_date) ||
    toLocalDateKey(task?.dueDate) ||
    toLocalDateKey(task?.end_date) ||
    toLocalDateKey(task?.endDate);
  return !!todayKey && !!dueKey && dueKey === todayKey;
};

const isAssignedToday = (task, todayKey) => {
  const assignedKey =
    toLocalDateKey(task?.assigned_at) ||
    toLocalDateKey(task?.assignedAt) ||
    toLocalDateKey(task?.created_at) ||
    toLocalDateKey(task?.createdAt);
  return !!todayKey && !!assignedKey && assignedKey === todayKey;
};

const Summarize = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [dailyChecklist, setDailyChecklist] = useState([]);
  const [tasksError, setTasksError] = useState("");
  const [checklistError, setChecklistError] = useState("");
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [loadingChecklist, setLoadingChecklist] = useState(true);

  const todayKey = useMemo(() => toLocalDateKey(new Date()), []);
  const todayLabel = useMemo(() => {
    const today = new Date();
    return today.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
    });
  }, []);

  useEffect(() => {
    let ignore = false;

    const loadUser = async () => {
      try {
        const res = await fetch(getApiUrl("api/auth/me"), {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error("Unable to verify user");
        }
        const data = await res.json();
        if (ignore) return;

        const resolvedUser = data?.user || data;
        const id =
          resolvedUser?.userId ??
          resolvedUser?.id ??
          resolvedUser?.sub ??
          null;

        if (id) {
          setUserId(id);
        } else {
          throw new Error("Missing user identifier");
        }
      } catch (err) {
        if (!ignore) {
          setTasksError(
            err?.message || "Unable to resolve current user information."
          );
          setLoadingTasks(false);
        }
      }
    };

    loadUser();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!userId) return;
    let ignore = false;

    const loadTasks = async () => {
      setLoadingTasks(true);
      setTasksError("");
      try {
        const res = await fetch(getApiUrl(`api/task/myTask/${userId}`), {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load tasks (status ${res.status})`);
        }
        const data = await res.json();
        if (ignore) return;

        const list = Array.isArray(data)
          ? data
          : data && Array.isArray(data.tasks)
            ? data.tasks
            : [];
        setTasks(list);
      } catch (err) {
        if (!ignore) {
          setTasksError(
            err?.message || "Something went wrong while loading tasks."
          );
          setTasks([]);
        }
      } finally {
        if (!ignore) {
          setLoadingTasks(false);
        }
      }
    };

    loadTasks();

    return () => {
      ignore = true;
    };
  }, [userId]);

  useEffect(() => {
    let ignore = false;

    const loadChecklist = async () => {
      setLoadingChecklist(true);
      setChecklistError("");
      try {
        const res = await fetch(getApiUrl("api/yesno/list"), {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`Failed to load daily checklist (status ${res.status})`);
        }
        const data = await res.json();
        if (ignore) return;

        if (data?.success) {
          setDailyChecklist(Array.isArray(data.data) ? data.data : []);
        } else if (Array.isArray(data)) {
          setDailyChecklist(data);
        } else {
          throw new Error(data?.message || "Daily checklist not available.");
        }
      } catch (err) {
        if (!ignore) {
          setChecklistError(
            err?.message || "Something went wrong while loading the daily checklist."
          );
          setDailyChecklist([]);
        }
      } finally {
        if (!ignore) {
          setLoadingChecklist(false);
        }
      }
    };

    loadChecklist();

    return () => {
      ignore = true;
    };
  }, []);

  const focusTasks = useMemo(() => {
    if (!Array.isArray(tasks)) return [];

    const scored = tasks
      .filter((task) => {
        const status = String(task?.status || "").trim().toLowerCase();
        return status !== "completed" && status !== "done";
      })
      .map((task) => {
        const assignedToday = isAssignedToday(task, todayKey);
        const dueToday = isDueToday(task, todayKey);
        const highPriority = isHighPriorityTask(task);
        return {
          task,
          assignedToday,
          dueToday,
          highPriority,
          weight:
            (dueToday ? 4 : 0) +
            (highPriority ? 2 : 0) +
            (assignedToday ? 1 : 0),
        };
      })
      .filter(({ assignedToday, dueToday, highPriority }) =>
        Boolean(assignedToday || dueToday || highPriority)
      );

    scored.sort((a, b) => {
      if (b.weight !== a.weight) return b.weight - a.weight;

      const dueDiff =
        (new Date(a.task?.due_date || a.task?.dueDate || 0).getTime() ||
          Number.MAX_SAFE_INTEGER) -
        (new Date(b.task?.due_date || b.task?.dueDate || 0).getTime() ||
          Number.MAX_SAFE_INTEGER);
      if (dueDiff !== 0) return dueDiff;

      const createdDiff =
        (new Date(a.task?.created_at || a.task?.createdAt || 0).getTime() || 0) -
        (new Date(b.task?.created_at || b.task?.createdAt || 0).getTime() || 0);
      return createdDiff;
    });

    return scored;
  }, [tasks, todayKey]);

  const totalFocusTasks = focusTasks.length;

  const todaysChecklistPool = useMemo(
    () => filterChecklistForToday(dailyChecklist, todayKey),
    [dailyChecklist, todayKey]
  );

  const todaysChecklist = useMemo(
    () =>
      [...todaysChecklistPool].sort((a, b) =>
        String(a?.plannedFromTime || "").localeCompare(
          String(b?.plannedFromTime || "")
        )
      ),
    [todaysChecklistPool]
  );

  const handleTaskClick = (taskId) => {
    if (taskId) {
      sessionStorage.setItem("mytask:focusTaskId", String(taskId));
    }
    navigate("/mytask");
  };

  const handleChecklistClick = (itemId) => {
    if (itemId) {
      sessionStorage.setItem("yesno:focusItemId", String(itemId));
    }
    navigate("/yesno?tab=execution");
  };

  const formatTaskMeta = (task) => {
    const parts = [];
    const priority = task?.priority;
    if (priority) {
      parts.push(priority);
    }
    const assignedRaw = task?.assigned_at || task?.assignedAt || task?.created_at || task?.createdAt;
    if (assignedRaw) {
      const assignedDate = new Date(assignedRaw);
      if (!Number.isNaN(assignedDate.getTime())) {
        parts.push(
          `Assigned ${assignedDate.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })}`
        );
      }
    }
    const dueRaw =
      task?.due_date ||
      task?.dueDate ||
      task?.end_date ||
      task?.endDate;
    if (dueRaw) {
      const dueDate = new Date(dueRaw);
      if (!Number.isNaN(dueDate.getTime())) {
        parts.push(
          `Target date ${dueDate.toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })}`
        );
      }
    }
    return parts.join(" • ");
  };

  const formatChecklistMeta = (item) => {
    const parts = [];
    if (item?.department) {
      parts.push(item.department);
    }
    if (item?.frequency) {
      parts.push(item.frequency);
    }
    if (item?.plannedFromTime && item?.plannedToTime) {
      parts.push(`${item.plannedFromTime} - ${item.plannedToTime}`);
    }
    return parts.join(" • ");
  };

  return (
    <DashboardLayout>
      <div className="summarize-page">
        <header className="summarize-header">
          <div>
            <h1>Today at a Glance</h1>
            <p>Keep track of the tasks and daily checklist items scheduled for today.</p>
          </div>
          <div className="summarize-date-chip" aria-label={`Today is ${todayLabel}`}>
            <FaCalendarDay />
            <span>{todayLabel}</span>
          </div>
        </header>

        <div className="summarize-grid">
          <section className="summarize-section summarize-section--tasks">
            <header className="summarize-section-header">
              <div>
                <h2>My Tasks</h2>
                <p>
                  {totalFocusTasks > 0
                    ? `You have ${totalFocusTasks} priority task${totalFocusTasks === 1 ? "" : "s"
                    } to review today.`
                    : "No flagged tasks need your attention right now."}
                </p>
              </div>
              <button
                type="button"
                className="summarize-link"
                onClick={() => navigate("/mytask")}
              >
                Open Tasks →
              </button>
            </header>

            <div className="summarize-content summarize-content--scrollable">
              {loadingTasks ? (
                <div className="summarize-state summarize-state--loading">
                  Loading today&apos;s tasks…
                </div>
              ) : tasksError ? (
                <div className="summarize-state summarize-state--error">
                  {tasksError}
                </div>
              ) : totalFocusTasks === 0 ? (
                <div className="summarize-state summarize-state--empty">
                  You are all caught up. No tasks match today&apos;s focus filters.
                </div>
              ) : (
                <ul className="summarize-list summarize-list--scrollable">
                  {focusTasks.map(({ task, assignedToday, dueToday, highPriority }, index) => {
                    const assigneeName = `${task?.GivenByFirstName || ""} ${task?.GivenByLastName || ""
                      }`
                      .replace(/\s+/g, " ")
                      .trim();

                    return (
                      <li key={task?.id ?? `task-${index}`}>
                        <button
                          type="button"
                          className="summarize-item"
                          onClick={() => handleTaskClick(task?.id)}
                        >
                          <div className="summarize-item-header">
                            <div className="summarize-item-title-group">
                              <span className="summarize-item-title">
                                {task?.title || "Untitled task"}
                              </span>
                              {(assignedToday || dueToday || highPriority) && (
                                <div className="summarize-item-tags">
                                  {assignedToday && (
                                    <span className="summarize-tag summarize-tag--new">New</span>
                                  )}
                                  {dueToday && (
                                    <span className="summarize-tag summarize-tag--due">Urgent</span>
                                  )}
                                  {highPriority && (
                                    <span className="summarize-tag summarize-tag--priority">
                                      High
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            <span
                              className={`summarize-status summarize-status--${getTaskStatusKey(
                                task?.status
                              )}`}
                            >
                              {task?.status || "Pending"}
                            </span>
                          </div>
                          <p className="summarize-item-meta">
                            {formatTaskMeta(task) || "No additional details"}
                          </p>
                          {assigneeName && (
                            <p className="summarize-item-subtext">
                              Assigned by {assigneeName}
                            </p>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="summarize-section summarize-section--checklist">
            <header className="summarize-section-header">
              <div>
                <h2>Daily Checklist</h2>
                <p>
                  {todaysChecklistPool.length > 0
                    ? `${todaysChecklistPool.length} item${todaysChecklistPool.length === 1 ? "" : "s"} planned for today.`
                    : "No checklist items scheduled for today."}
                </p>
              </div>
              <button
                type="button"
                className="summarize-link"
                onClick={() => navigate("/yesno?tab=execution")}
              >
                Open Checklist →
              </button>
            </header>

            <div className="summarize-content summarize-content--scrollable">
              {loadingChecklist ? (
                <div className="summarize-state summarize-state--loading">
                  Loading today&apos;s checklist…
                </div>
              ) : checklistError ? (
                <div className="summarize-state summarize-state--error">
                  {checklistError}
                </div>
              ) : todaysChecklist.length === 0 ? (
                <div className="summarize-state summarize-state--empty">
                  No checklist items need your attention right now.
                </div>
              ) : (
                <ul className="summarize-list summarize-list--scrollable">
                  {todaysChecklist.map((item, index) => (
                    <li key={item?.id ?? `checklist-${index}`}>
                      <button
                        type="button"
                        className="summarize-item"
                        onClick={() => handleChecklistClick(item?.id)}
                      >
                        <div className="summarize-item-header">
                          <span className="summarize-item-title">
                            {item?.name || "Checklist item"}
                          </span>
                          {item?.frequency && (
                            <span className="summarize-badge">
                              {item.frequency}
                            </span>
                          )}
                        </div>
                        <p className="summarize-item-meta">
                          {formatChecklistMeta(item) || "No extra details"}
                        </p>
                        {item?.remarks && (
                          <p className="summarize-item-subtext">
                            {item.remarks}
                          </p>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Summarize;
